import { useState, useEffect } from "react";
import styles from "./complaints.module.css";
import { Typography, Dialog, DialogTitle, DialogContent, Snackbar, Slide, IconButton, TablePagination } from "@mui/material";
import { Home, X, ArrowLeft } from "react-feather";
import Helmet from "react-helmet";
import Footer from "../../components/Footer";
import { useAuth0, withAuthenticationRequired } from "@auth0/auth0-react";
import Loader from "../../components/Loader";
import ComplaintFilter from "./components/ComplaintFilter";
import ComplaintCard from "./components/ComplaintCard";
import ComplaintMobileCard from "./components/ComplaintMobileCard";
import AddComplaint from "./components/AddComplaint";
import axios from "axios";
import { TableLoader } from "../../components/LoaderC";
import { AddLogCallAPI } from "../../components/AddLogs";
import { toast } from 'react-smart-toaster';
import dayjs from "dayjs";

const Complaints = () => {
  const { getAccessTokenSilently } = useAuth0();
  const [loading, setLoading] = useState(false);
  const [contentLoading, setContentLoading] = useState(true);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState("");
  const [filterData, setFilterData] = useState({ searchKey: "", property: "", apartment: "", status: "", priority: "" });
  const [sortData, setSortData] = useState("");
  const [allData, setAllData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(8);

  useEffect(() => {
    const getRequests = async () => {
      const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });

      axios(process.env.REACT_APP_CASE_API_URL + "v1/complaints", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }).then((response) => {
        setContentLoading(false);

        let allDataTemp = response.data.data?.map(item => {
          if (item?.category === 'Noise')
            return { ...item, image: '/static/img/noise.svg' };
          if (item?.category === 'Garbage Removal')
            return { ...item, image: '/static/img/garbage.svg' };
          return { ...item, image: '/static/img/other.svg' };
        })

        setAllData(allDataTemp);
      }).catch((error) => {
        setContentLoading(false);
      });
    }
    getRequests();
  }, [getAccessTokenSilently]);

  useEffect(() => {
    let filteredDataTemp = allData;

    if (filterData.searchKey !== '') {
      let inputVal = filterData.searchKey;
      inputVal = inputVal.toLowerCase();
      filteredDataTemp = filteredDataTemp.filter((item) => {
        return item?.title.toLowerCase().includes(inputVal) || item?.complaint_no.toLowerCase().includes(inputVal) || item?.tenant_name.toLowerCase().includes(inputVal) || item?.property_name.toLowerCase().includes(inputVal) || item?.apt_name.toLowerCase().includes(inputVal);
      });
    }

    if (filterData.property !== "" && filterData.property !== "all") {
      filteredDataTemp = filteredDataTemp.filter((i) => i.property_id === filterData.property);
    }

    if (filterData.apartment !== "" && filterData.apartment !== "all") {
      filteredDataTemp = filteredDataTemp.filter((i) => i.apt_id === filterData.apartment);
    }

    if (filterData.status !== "" && filterData.status !== "all") {
      if (filterData.status === "Open") {
        filteredDataTemp = filteredDataTemp.filter((i) => i.current_status === 0 && i.is_archived === 0);
      }
      if (filterData.status === "Resolved") {
        filteredDataTemp = filteredDataTemp.filter((i) => i.current_status === 2 && i.is_archived === 0);
      }
      if (filterData.status === "Cancelled") {
        filteredDataTemp = filteredDataTemp.filter((i) => i.current_status === 3 && i.is_archived === 0);
      }
    }

    if (filterData.priority !== "" && filterData.priority !== "all") {
      if (filterData.priority === "Low") {
        filteredDataTemp = filteredDataTemp.filter((i) => i.priority === 1);
      }
      if (filterData.priority === "Medium") {
        filteredDataTemp = filteredDataTemp.filter((i) => i.priority === 2);
      }
      if (filterData.priority === "High") {
        filteredDataTemp = filteredDataTemp.filter((i) => i.priority === 3);
      }
    }

    setFilteredData(filteredDataTemp);
  }, [allData, filterData]);

  const changeCStatus = async (item, cVal) => {
    if (item?.current_status !== cVal) {
      setLoading(true);

      const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });

      axios.put(process.env.REACT_APP_CASE_API_URL + 'v1/complaints/' + item?.id, { 'current_status': cVal }, {
        headers: {
          Authorization: `Bearer ${token}`,
        }
      }).then(response => {
        setLoading(false);
        setSnackbarMsg(response.data.message);
        setSnackbarOpen(true);

        setAllData(prev => {
          return prev?.map(i => {
            if (i?.id === item?.id)
              return { ...i, current_status: cVal };
            return i;
          })
        })

        let logData = {
          'title': 'Complaint status has been updated',
          'description': [
            'Name: ' + response.data.data.title,
            'Complaints number: ' + response.data.data.complaint_no,
            'Apartment: ' + response.data.data.property_name + ' - ' + response.data.data.apt_name,
            'Tenant: ' + response.data.data.tenant_name
          ]
        }
        AddLogCallAPI(logData, token);
      }).catch(error => {
        setLoading(false);
        if (typeof error.response !== 'undefined')
          toast.error(error.response.data.message);
      });
    }
  }

  const onSubmitLocal = (data) => {
    let img = '/static/img/other.svg';
    if (data?.category === 'Noise')
      img = '/static/img/noise.svg';
    if (data?.category === 'Garbage Removal')
      img = '/static/img/garbage.svg';

    data = { ...data, image: img };

    setAllData(prev => {
      return [data]?.concat(prev);
    })
  }

  return (<>
    {loading && <Loader />}
    <Helmet title="Complaints" />
    <div className={`${styles.pageTitleWrap}`}>
      <Typography display="inline" className={`${styles.pageTitle}`}>Complaints</Typography>
      <nav aria-label="breadcrumb">
        <ol className={`${styles.breadcrumb}`}>
          <li className={`${styles.breadcrumbItem}`}><span><Home /></span>Cases</li>
          <li className={`${styles.breadcrumbItem} ${styles.active}`}>Complaints</li>
        </ol>
      </nav>
    </div>

    <div className={`${styles.BodyCon}`}>
      <div className={`${styles.mainCardDiv}`}>
        <div className={`${styles.mainCard}`}>

          {contentLoading && (<div className="Loader TableLoader">
            <TableLoader />
          </div>)}

          {!contentLoading && (<div className="control-pane">
            <div className={`${styles.Row}`}>

              <ComplaintFilter setAddModalOpen={setAddModalOpen.bind(this)} setFilterData={setFilterData.bind(this)} setSortData={setSortData.bind(this)} setPage={setPage.bind(this)} />

              {filteredData.length > 0 && <>
                <div className={`${styles.NotesRow}`}>
                  {filteredData?.sort((a, b) => {
                    if (sortData === 'erliest') {
                      if (dayjs(a?.created_at).isAfter(b?.created_at)) {
                        return 1;
                      }
                      return -1;
                    } else {
                      if (dayjs(a?.created_at).isAfter(b?.created_at)) {
                        return -1;
                      }
                      return 1;
                    }
                  })?.map((item, index) => {
                    return (perPage === -1 || (index >= page * perPage && index < page * perPage + perPage)) ? <ComplaintCard item={item} changeCStatus={changeCStatus.bind(this)} /> : null;
                  })}

                  <div className={`${styles.NotesRow} Pagination TablePaginationNew List`}>
                    <TablePagination
                      count={filteredData.length}
                      color="primary"
                      page={page}
                      rowsPerPage={perPage}
                      onPageChange={(e, v) => setPage(v)}
                      rowsPerPageOptions={[
                        { label: '4', value: 4 },
                        { label: '8', value: 8 },
                        { label: '16', value: 16 },
                        { label: 'All', value: -1 },
                      ]}
                      onRowsPerPageChange={(e) => {
                        setPage(0);
                        setPerPage(e.target.value);
                      }}
                      labelRowsPerPage={'Complaints per Page:'}
                    />
                  </div>
                </div>

                <div className={`${styles.ListMobileView}`}>
                  {filteredData?.sort((a, b) => {
                    if (sortData === 'erliest') {
                      if (dayjs(a?.created_at).isAfter(b?.created_at)) {
                        return 1;
                      }
                      return -1;
                    } else {
                      if (dayjs(a?.created_at).isAfter(b?.created_at)) {
                        return -1;
                      }
                      return 1;
                    }
                  })?.map((item, index) => {
                    return <ComplaintMobileCard item={item} changeCStatus={changeCStatus.bind(this)} />
                  })}
                </div>
              </>}

              {filteredData.length === 0 && (<div className={`${styles.NoDataMain}`}>
                <div className={`${styles.NoDataIMG}`}>
                  <img src="/static/img/no-request.png" alt="No Data" />
                </div>
                <p className={`${styles.NoDataText}`}>All caught up!</p>
                <p className={`${styles.NoDataTextSub}`}>There are currently no complaints to show.</p>
              </div>)}

            </div>
          </div>)}


        </div>
      </div>
    </div>

    <Dialog
      open={addModalOpen}
      fullWidth
      maxWidth="sm"
      onClose={(e) => setAddModalOpen(false)}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      className="NewPopUp MobileForm"
    >
      <DialogTitle id="alert-dialog-title">
        <button className={`${styles.BackAddBU}`} onClick={(e) => setAddModalOpen(false)}><ArrowLeft /></button>
        New Complaint
        <button onClick={(e) => setAddModalOpen(false)}><X /></button>
      </DialogTitle>
      <DialogContent className={`${styles.RequestBodyPop}`}>
        <AddComplaint setAddModalOpen={setAddModalOpen.bind(this)} tenant_id={0} setLoading={setLoading.bind(this)} setSnackbarMsg={setSnackbarMsg.bind(this)} setSnackbarOpen={setSnackbarOpen.bind(this)} onSubmitLocal={onSubmitLocal.bind(this)} complaintData={null} />
      </DialogContent>
    </Dialog>

    <Snackbar
      open={snackbarOpen}
      onClose={(e) => setSnackbarOpen(false)}
      TransitionComponent={Slide}
      message={snackbarMsg}
      autoHideDuration={10000}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      action={
        <IconButton
          size="small"
          aria-label="close"
          color="inherit"
          onClick={(e) => setSnackbarOpen(false)}
        >
          <X />
        </IconButton>
      }
    />

    <Footer />
  </>);
};

export default withAuthenticationRequired(Complaints, {
  onRedirecting: () => <Loader />,
});